// src/api/chunks.js
import { supabase } from '../supabaseClient';
import { debug } from '../helpers/debug';

let _supportsContentMd = null;
let _supportsChunkSummaryColumns = null;

const isMissingColumnError = (error = null) => {
  const msg = String(error?.message || '').toLowerCase();
  return error?.code === '42703' || (msg.includes('column') && msg.includes('content_md') && msg.includes('does not exist'));
};

const isMissingChunkSummaryColumnError = (error = null) => {
  const msg = String(error?.message || '').toLowerCase();
  if (error?.code === '42703') return true;
  return msg.includes('column')
    && msg.includes('does not exist')
    && (msg.includes('summary_md') || msg.includes('summarized_at') || msg.includes('char_length'));
};

const isMissingRpcArgError = (error = null) => {
  const msg = String(error?.message || '').toLowerCase();
  return msg.includes('upsert_doc_chunks') && (
    msg.includes('could not find the function') ||
    msg.includes('does not exist') ||
    msg.includes('no function matches')
  );
};

const loadStoredRowsByHashes = async (targetHashes = []) => {
  if (!Array.isArray(targetHashes) || targetHashes.length === 0) {
    return { rows: [], supportsContentMd: _supportsContentMd === true };
  }

  const tryWithMd = async () => {
    const result = await supabase
      .from('chunks')
      .select('chunk_hash, content_md, char_length')
      .in('chunk_hash', targetHashes);

    if (result.error) throw result.error;
    _supportsContentMd = true;
    return result.data || [];
  };

  const tryWithoutMd = async () => {
    const result = await supabase
      .from('chunks')
      .select('chunk_hash')
      .in('chunk_hash', targetHashes);

    if (result.error) throw result.error;
    _supportsContentMd = false;
    return (result.data || []).map(row => ({ ...row, content_md: null, char_length: null }));
  };

  if (_supportsContentMd === false) {
    return { rows: await tryWithoutMd(), supportsContentMd: false };
  }

  try {
    return { rows: await tryWithMd(), supportsContentMd: true };
  } catch (error) {
    if (!isMissingColumnError(error)) throw error;
    debug('apiSaveDocChunks', { type: 'warn' }, 'content_md column missing in chunks table; using compatibility mode');
    return { rows: await tryWithoutMd(), supportsContentMd: false };
  }
};

const callUpsertDocChunks = async (docId, chunkPayloads, docMd) => {
  const callWithDocMd = async () => {
    const { error } = await supabase.rpc('upsert_doc_chunks', {
      p_doc_id: docId,
      p_chunks: chunkPayloads,
      p_doc_md: docMd || null
    });
    if (error) throw error;
  };

  const callLegacy = async () => {
    const { error } = await supabase.rpc('upsert_doc_chunks', {
      p_doc_id: docId,
      p_chunks: chunkPayloads
    });
    if (error) throw error;
  };

  try {
    await callWithDocMd();
  } catch (error) {
    if (!isMissingRpcArgError(error)) throw error;
    debug('apiSaveDocChunks', { type: 'warn' }, 'upsert_doc_chunks(p_doc_md) unavailable; falling back to legacy 2-arg RPC');
    await callLegacy();
  }
};

/**
 * Save chunks for a specific document with differential updates
 * Only updates chunks that have changed by comparing chunk_hash and content
 * @param {number} docId - Document ID
 * @param {Array<Object>} chunksData - Array of { content_json, char_length, chunk_hash, hash }
 * @param {Array<Object>} prevRefs - Optional previous chunk refs { chunk_hash, char_length, hash } to avoid loading
 * @returns {Promise<Array<Object>>} - Array of chunk records with IDs
 */
export async function apiSaveDocChunks(docId, chunksData, prevRefs = null, docMd = null) {
  try {
    // Step 1: Get existing chunks info from prevRefs (stored in docs content_json)
    let existingChunks;
    if (prevRefs) {
      existingChunks = prevRefs.map(ref => ({
        chunk_hash: ref.chunk_hash,
        hash: ref.hash,
        char_length: ref.char_length
      }));
    } else {
      // Fallback: load from docs.content_json if prevRefs not provided
      const { data: doc, error } = await supabase
        .from('docs')
        .select('content_json')
        .eq('id', docId)
        .single();
      
      if (error) throw error;
      
      existingChunks = (doc?.content_json?.chunks || []).map(ref => ({
        chunk_hash: ref.chunk_hash,
        hash: ref.hash,
        char_length: ref.char_length
      }));
      
      debug('apiSaveDocChunks', { type: 'audit', level: 3 }, `Loaded ${existingChunks.length} chunk refs from docs content_json for doc:${docId}`);
    }
    
    // Step 2: Determine what needs to be updated
    const toDelete = [];
    const toInsert = [];
    const toUpdate = [];
    const chunkPayloads = [];

    // Load actual stored chunk hashes to detect desync cases where refs exist
    // in docs.content_json but rows are missing from chunks table.
    const targetHashes = (chunksData || []).map(chunk => chunk?.chunk_hash).filter(Boolean);
    const { rows: storedRows, supportsContentMd } = await loadStoredRowsByHashes(targetHashes);
    const storedHashSet = new Set((storedRows || []).map(row => row?.chunk_hash).filter(Boolean));
    const storedRowByHash = new Map((storedRows || []).map(row => [row?.chunk_hash, row]));
    
    // Build a map of existing chunks by chunk_hash
    const existingMap = new Map(existingChunks.map(c => [c.chunk_hash, c]));
    
    // Compare new chunks with existing
    for (let i = 0; i < chunksData.length; i++) {
      const newChunk = chunksData[i];
      const existing = existingMap.get(newChunk.chunk_hash);
      // Get previous hash from existing chunks
      const prevHash = existing?.hash;
      const rowExistsInTable = storedHashSet.has(newChunk.chunk_hash);
      const storedRow = storedRowByHash.get(newChunk.chunk_hash) || null;
      const rowHasMissingMd = rowExistsInTable && (!storedRow?.content_md || !String(storedRow.content_md).trim());
      if (!existing) {
        toInsert.push({
          doc_id: docId,
          chunk_hash: newChunk.chunk_hash,
          content_json: newChunk.content_json,
          index: i
        });
        const payload = {
          chunk_hash: newChunk.chunk_hash,
          content_json: newChunk.content_json,
          char_length: Number.isFinite(Number(newChunk.char_length))
            ? Number(newChunk.char_length)
            : null
        };
        if (supportsContentMd) payload.content_md = newChunk.content_md || null;
        chunkPayloads.push(payload);
        const firstNode = newChunk.content_json?.content?.[0];
        const firstNodeType = firstNode ? (firstNode.type === 'heading' ? `heading-h${firstNode.attrs?.level || 1}` : firstNode.type) : 'unknown';
        debug('apiSaveDocChunks', { type: 'log' }, `CREATE chunk: ${newChunk.chunk_hash} hash: ${newChunk.hash} chars: ${newChunk.char_length} reason: ${newChunk.splitReason || 'unknown'} first: ${firstNodeType}`);
      } else {
        if (newChunk.hash !== undefined && newChunk.hash !== prevHash) {
          toUpdate.push({
            chunk_hash: newChunk.chunk_hash,
            content_json: newChunk.content_json,
            index: i
          });
          const payload = {
            chunk_hash: newChunk.chunk_hash,
            content_json: newChunk.content_json,
            char_length: Number.isFinite(Number(newChunk.char_length))
              ? Number(newChunk.char_length)
              : null
          };
          if (supportsContentMd) payload.content_md = newChunk.content_md || null;
          chunkPayloads.push(payload);
          const firstNode = newChunk.content_json?.content?.[0];
          const firstNodeType = firstNode ? (firstNode.type === 'heading' ? `heading-h${firstNode.attrs?.level || 1}` : firstNode.type) : 'unknown';
          debug('apiSaveDocChunks', { type: 'log' }, `UPDATE chunk: ${newChunk.chunk_hash} hash: ${newChunk.hash} chars: ${newChunk.char_length} (was: ${prevHash}) reason: ${newChunk.splitReason || 'unknown'} first: ${firstNodeType}`);
        } else if (!rowExistsInTable) {
          // Self-heal: chunk is referenced/unchanged but missing in table.
          toUpdate.push({
            chunk_hash: newChunk.chunk_hash,
            content_json: newChunk.content_json,
            index: i
          });
          const payload = {
            chunk_hash: newChunk.chunk_hash,
            content_json: newChunk.content_json,
            char_length: Number.isFinite(Number(newChunk.char_length))
              ? Number(newChunk.char_length)
              : null
          };
          if (supportsContentMd) payload.content_md = newChunk.content_md || null;
          chunkPayloads.push(payload);
          debug('apiSaveDocChunks', { type: 'warn' }, `REPAIR chunk: ${newChunk.chunk_hash} was missing in chunks table; re-upserting`);
        } else if (rowHasMissingMd && newChunk.content_md) {
          toUpdate.push({
            chunk_hash: newChunk.chunk_hash,
            content_json: newChunk.content_json,
            index: i
          });
          const payload = {
            chunk_hash: newChunk.chunk_hash,
            content_json: newChunk.content_json,
            char_length: Number.isFinite(Number(newChunk.char_length))
              ? Number(newChunk.char_length)
              : null
          };
          if (supportsContentMd) payload.content_md = newChunk.content_md;
          chunkPayloads.push(payload);
          debug('apiSaveDocChunks', { type: 'log' }, `HEAL markdown: ${newChunk.chunk_hash} content_md was missing; backfilled`);
        } else {
          const firstNode = newChunk.content_json?.content?.[0];
          const firstNodeType = firstNode ? (firstNode.type === 'heading' ? `heading-h${firstNode.attrs?.level || 1}` : firstNode.type) : 'unknown';
          debug('apiSaveDocChunks', { type: 'log' }, `UNCHANGED chunk: ${newChunk.chunk_hash} hash: ${newChunk.hash} chars: ${newChunk.char_length} reason: ${newChunk.splitReason || 'unknown'} first: ${firstNodeType}`);
        }
      }
      existingMap.delete(newChunk.chunk_hash);
    }
    
    // Any remaining in existingMap are no longer needed
    for (const chunk of existingMap.values()) {
      toDelete.push(chunk.chunk_hash);
    }
    
    debug('apiSaveDocChunks', { type: 'log' }, `Summary: ✨ ${toInsert.length} created | 🔄 ${toUpdate.length} updated | 🗑️ ${toDelete.length} deleted | ⚪ ${chunksData.length - toInsert.length - toUpdate.length} unchanged`);
    
    // Step 3: Execute operations
    if (chunkPayloads.length > 0 || docMd !== null) {
      await callUpsertDocChunks(docId, chunkPayloads, docMd);
    }
    
    if (toDelete.length > 0) {
      const { error: deleteError } = await supabase.rpc('delete_doc_chunks', {
        p_doc_id: docId,
        p_chunk_hashes: toDelete
      });
      if (deleteError) throw deleteError;
    }
    
    // Step 4: Return chunk metadata instead of full records
    // Since callers only use count and basic metadata, avoid fetching full content
    const chunkMetadata = chunksData.map((chunk, index) => ({
      id: null, // Not needed for metadata
      chunk_hash: chunk.chunk_hash,
      char_length: chunk.char_length,
      index: index
    }));
    
    return chunkMetadata;
  } catch (err) {
    debug('[API] saveDocChunks error:', { type: 'error' }, err);
    throw err;
  }
}

/**
 * Load all chunks for a specific document
 * @param {number} docId - Document ID  
 * @returns {Promise<Array<Object>>} - Array of chunk records ordered by chunk_index
 */
export async function apiLoadChunks(docId) {
  try {
    const { data, error } = await supabase
      .from('chunks')
      .select('chunk_hash, content_json, summary_md, summarized_at, char_length, updated_at')
      .eq('doc_id', docId);
    
    if (error) throw error;
    
    debug('apiLoadChunks', { type: 'log' }, `Loaded ${data?.length || 0} chunks for doc ${docId}: ${data?.map(c => c.chunk_hash).join(', ') || 'none'}`);
    
    return data || [];
  } catch (err) {
    debug('[API] loadChunks error:', { type: 'error' }, err);
    return [];
  }
}

export async function apiLoadChunksByHashes(chunkHashes = []) {
  const normalized = Array.from(new Set((chunkHashes || []).map(hash => String(hash || '').trim()).filter(Boolean)));
  if (normalized.length === 0) return [];

  try {
    if (_supportsContentMd === false) {
      const { data, error } = await supabase
        .from('chunks')
        .select('doc_id, chunk_hash, content_json, updated_at')
        .in('chunk_hash', normalized);

      if (error) throw error;
      return (data || []).map(row => ({
        ...row,
        content_md: null,
        summary_md: null,
        summarized_at: null,
        char_length: null
      }));
    }

    if (_supportsChunkSummaryColumns !== false) {
      const withSummary = await supabase
        .from('chunks')
        .select('doc_id, chunk_hash, content_json, content_md, summary_md, summarized_at, char_length, updated_at')
        .in('chunk_hash', normalized);

      if (!withSummary.error) {
        _supportsContentMd = true;
        _supportsChunkSummaryColumns = true;
        return withSummary.data || [];
      }

      if (!isMissingChunkSummaryColumnError(withSummary.error)) throw withSummary.error;
      _supportsChunkSummaryColumns = false;
    }

    const withMd = await supabase
      .from('chunks')
      .select('doc_id, chunk_hash, content_json, content_md, updated_at')
      .in('chunk_hash', normalized);

    if (!withMd.error) {
      _supportsContentMd = true;
      return (withMd.data || []).map(row => ({
        ...row,
        summary_md: null,
        summarized_at: null,
        char_length: null
      }));
    }

    if (!isMissingColumnError(withMd.error)) throw withMd.error;

    debug('apiLoadChunksByHashes', { type: 'warn' }, 'content_md column missing in chunks table; returning content_json only');
    const fallback = await supabase
      .from('chunks')
      .select('doc_id, chunk_hash, content_json, updated_at')
      .in('chunk_hash', normalized);

    if (fallback.error) throw fallback.error;
    _supportsContentMd = false;
    return (fallback.data || []).map(row => ({
      ...row,
      content_md: null,
      summary_md: null,
      summarized_at: null,
      char_length: null
    }));
  } catch (err) {
    debug('[API] loadChunksByHashes error:', { type: 'error' }, err);
    return [];
  }
}

export async function apiSaveChunkSummariesByHash(items = []) {
  const rows = Array.isArray(items)
    ? items.map((item) => {
      const chunkHash = String(item?.chunkHash || item?.chunk_hash || '').trim();
      if (!chunkHash) return null;
      const summaryMd = String(item?.summaryMd || item?.summary_md || '').trim();
      if (!summaryMd) return null;
      const summarizedAtRaw = item?.summarizedAt || item?.summarized_at;
      const summarizedAt = summarizedAtRaw ? new Date(summarizedAtRaw).toISOString() : new Date().toISOString();
      const charLength = Number(item?.charLength || item?.char_length);
      return {
        chunkHash,
        updates: {
          summary_md: summaryMd,
          summarized_at: summarizedAt,
          ...(Number.isFinite(charLength) && charLength > 0 ? { char_length: Math.trunc(charLength) } : {}),
          updated_at: new Date().toISOString()
        }
      };
    }).filter(Boolean)
    : [];

  if (rows.length === 0) return [];

  const saved = [];
  for (const row of rows) {
    try {
      const { data, error } = await supabase
        .from('chunks')
        .update(row.updates)
        .eq('chunk_hash', row.chunkHash)
        .select('chunk_hash, summary_md, summarized_at')
        .maybeSingle();

      if (error) {
        if (isMissingChunkSummaryColumnError(error)) return saved;
        throw error;
      }
      if (data) saved.push(data);
    } catch (error) {
      if (isMissingChunkSummaryColumnError(error)) {
        return saved;
      }
      throw error;
    }
  }

  return saved;
}

/**
 * Load chunks by IDs
 * @param {Array<number>} chunkHashs - Array of chunk IDs
 * @returns {Promise<Array<Object>>} - Array of chunk records in order
 */
export async function apiLoadChunksByIds(chunkHashs) {
  debug('apiLoadChunksByIds', { type: 'audit', level: 2 }, { count: chunkHashs.length });
  
  if (!chunkHashs || chunkHashs.length === 0) {
    return [];
  }
  
  try {
    const { data, error } = await supabase
      .from('chunks')
      .select('id, content_json')
      .in('id', chunkHashs);
    
    if (error) throw error;
    
    // Return chunks in the order specified by chunkHashs
    const chunkMap = new Map((data || []).map(chunk => [chunk.id, chunk]));
    return chunkHashs.map(id => chunkMap.get(id)).filter(Boolean);
  } catch (err) {
    debug('[API] loadChunksByIds error:', { type: 'error' }, err);
    return [];
  }
}

/**
 * Load specific range of chunks for a document
 * @param {number} docId - Document ID
 * @param {number} fromIndex - Start index (inclusive)
 * @param {number} toIndex - End index (inclusive)
 * @returns {Promise<Array<Object>>} - Array of chunk records
 */
export async function apiLoadDocChunkRange(docId, fromIndex, toIndex) {
  debug('apiLoadDocChunkRange', { type: 'audit', level: 2 }, { docId, fromIndex, toIndex });
  
  try {
    // First get the doc's chunk_hashs array
    const { data: doc, error: docError } = await supabase
      .from('docs')
      .select('chunk_hashs')
      .eq('id', docId)
      .single();
    
    if (docError) throw docError;
    
    const chunkHashs = doc?.chunk_hashs || [];
    if (chunkHashs.length === 0) return [];
    
    // Extract the range
    const rangeIds = chunkHashs.slice(fromIndex, toIndex + 1);
    
    // Load those chunks
    return await apiLoadChunks(rangeIds);
  } catch (err) {
    debug('[API] loadDocChunkRange error:', { type: 'error' }, err);
    return [];
  }
}

/**
 * Update embedding for a chunk
 * @param {number} chunkHash - Chunk ID
 * @param {Array<number>} embedding - Embedding vector
 * @returns {Promise<Object>} - Updated chunk record
 */
export async function apiUpdateChunkEmbedding(chunkHash, embedding) {
  debug('apiUpdateChunkEmbedding', { type: 'audit', level: 3 }, { chunkHash });
  
  try {
    const { data, error } = await supabase
      .from('chunks')
      .update({ embedding })
      .eq('id', chunkHash)
      .select()
      .single();
    
    if (error) throw error;
    
    debug('apiUpdateChunkEmbedding.complete', { type: 'audit', level: 3 }, { chunkHash });
    
    return data;
  } catch (err) {
    debug('[API] updateChunkEmbedding error:', { type: 'error' }, err);
    throw err;
  }
}

/**
 * Delete all chunks for a specific document
 * @param {number} docId - Document ID
 * @returns {Promise<boolean>} - true when complete
 */
export async function apiDeleteDocChunks(docId) {
  try {
    const { error } = await supabase.rpc('delete_doc_chunks', {
      p_doc_id: docId
    });
    if (error) throw error;
    debug('apiDeleteDocChunks', { type: 'log' }, `Deleted chunks for doc ${docId}`);
    return true;
  } catch (err) {
    debug('[API] deleteDocChunks error:', { type: 'error' }, err);
    throw err;
  }
}

